import React, { useState } from "react";
import { useFormikContext } from "formik";
import orderBy from "lodash/orderBy";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import useQueryParams from "src/hooks/useQueryParams";

import ImageModal from "../../../components/images/ImageModal";
import useNotification from "../../../hooks/notification";
import ImageDND from "./ImageDND";
import ImagesSlider from "./ImagesSlider";
import {
  ADD_ATTACHMENT_INTERNAL_WORK_ORDER_ITEMS,
  REMOVE_ATTACHMENT_INTERNAL_WORK_ORDER_ITEMS,
  UPDATE_ATTACHMENT_INTERNAL_WORK_ORDER_ITEMS,
  BULK_CREATE_ASSET_IMAGES_MUTATION,
  DELETE_IMAGE_MUTATION,
  UPDATE_IMAGE_MUTATION,
} from "src/constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import { useLoading } from "../../inventory/requests/orders/internal-work-order-guided-wizard/Items/ItemDetails/LoadingProvider";
import { INTERNAL_WORK_ORDER_QUERY, ASSET_QUERY } from "src/constants/graphql/queries";

export default function ImagesInternal({ asset, internalWorkOrderId }) {
  const { notifyError, notifySuccess } = useNotification();
  const [loading, setLoading] = useState(false);
  const { errors = {}, submitCount = 0 } = useFormikContext();
  const list = orderBy(asset?.images || [], v => v.default, "desc");
  const [open, setOpen] = useState(null);
  const defaultIndex = list.findIndex(v => v.default);
  const [index, setIndex] = useState(defaultIndex === -1 ? 0 : defaultIndex);
  const params = useQueryParams();
  const internalWorkOrderItemId = params.get("internalWorkOrderItemId");
  
  const refetchQueries = [
    ...(internalWorkOrderId ? [{ query: INTERNAL_WORK_ORDER_QUERY, variables: { id: internalWorkOrderId } }] : []),
    ...(asset?.id ? [{ query: ASSET_QUERY, variables: { id: asset.id } }] : []),
  ];

  const [bulkCreateAssetImages] = useMutation(BULK_CREATE_ASSET_IMAGES_MUTATION, {
    refetchQueries,
  });
  const [deleteImage] = useMutation(DELETE_IMAGE_MUTATION, {
    refetchQueries,
  });
  const [updateImage] = useMutation(UPDATE_IMAGE_MUTATION, {
    refetchQueries,
  });
  const [addAttachmentInternalWorkOrderItems] = useMutation(ADD_ATTACHMENT_INTERNAL_WORK_ORDER_ITEMS);
  const [removeAttachmentInternalWorkOrderItems] = useMutation(REMOVE_ATTACHMENT_INTERNAL_WORK_ORDER_ITEMS);
  const [updateAttachmentInternalWorkOrderItems] = useMutation(UPDATE_ATTACHMENT_INTERNAL_WORK_ORDER_ITEMS);
  const { setLoading: setFormLoading } = useLoading();
  const hasError = errors.photo && submitCount > 0;

  const handleOpen = () => {
    if (list.length > 0) {
      setOpen(true);
    }
  };

  const handleBulkCreateImages = async ({ variables, onSuccess, onFailure }) => {
    setFormLoading(true);

    try {
      const response = await bulkCreateAssetImages({
        variables: { assetId: asset?.id, images: variables.images },
      });

      if (response?.data?.bulkCreateAssetImages) {
        const result = response.data.bulkCreateAssetImages;
        
        if (internalWorkOrderItemId) {
          const { images } = result.asset;
          const newImages = images.slice(-variables.images.length);

          await Promise.all(
            variables.images.map((file, index) => {
              if (index < newImages.length) {
                return addAttachmentInternalWorkOrderItems({
                  variables: {
                    file,
                    internalWorkOrderItemId,
                    pairId: +newImages[index].id,
                  },
                  refetchQueries,
                });
              }
              return Promise.resolve();
            })
          );
        }
        
        notifySuccess("Images have been uploaded");
        onSuccess?.(result);
      }
      setFormLoading(false);
      return response;
    } catch (errors) {
      onFailure?.(errors);
      setFormLoading(false);
      throw errors;
    }
  };

  const handleUpload = async images => {
    setLoading(true);
    await handleBulkCreateImages({
      variables: { assetId: asset.id, images },
      onFailure: errors => {
        notifyError(errors);
        setLoading(false);
      },
      onSuccess: () => {
        setLoading(false);
      },
    });
  };

  const handleRemove = (data, currentImage) => {
    setIndex(0);
    if (internalWorkOrderItemId) {
      deleteImage({ variables: { id: currentImage.pairId || data.variables.id } });
      removeAttachmentInternalWorkOrderItems({
        variables: { id: data.variables.id, internalWorkOrderItemId },
        refetchQueries,
      });
    } else {
      deleteImage({ variables: { id: data.variables.id } });
    }
  };

  const handleUpdateImage = (data, currentImage) => {
    if (internalWorkOrderItemId) {
      updateImage({ variables: { ...data.variables, id: currentImage.pairId || data.variables.id } });
      updateAttachmentInternalWorkOrderItems({
        variables: data.variables,
        refetchQueries,
      });
    } else {
      updateImage({ variables: data.variables });
    }
  };

  const handleDefaultUpload = e => {
    const files = Array.from(e.target.files);
    if (files.length) {
      handleUpload(files);
    }
  };

  return (
    <Box px={{ md: 5, xs: 2 }}>
      <Box width="100%" sx={{ aspectRatio: "5/4" }}>
        {loading ? (
          <Skeleton variant="rectangular" height="100%" width="100%" />
        ) : (
          <Box position="relative" onClick={handleOpen}>
            {list.length === 0 && <ImageDND onDrop={handleUpload} />}
            <ImagesSlider list={list} index={index} onChangeIndex={setIndex} />
          </Box>
        )}
      </Box>
      {open && list[index] && (
        <ImageModal
          open
          closeOnSave={true}
          selectedId={list[index].id}
          asset={asset}
          images={list}
          onClose={() => setOpen(false)}
          onRemove={handleRemove}
          onUpdate={handleUpdateImage}
          onCreate={handleBulkCreateImages}
        />
      )}
      <Box mt={1.5}>
        <input
          type="file"
          accept="image/*"
          id="internal-button"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleDefaultUpload}
        />
        <label htmlFor="internal-button" style={{ width: "100%", margin: 0 }}>
          <Button
            fullwidth
            variant="outlined"
            color={hasError ? "error" : "primary"}
            fullWidth
            component="span"
          >
            <AddAPhotoIcon />
          </Button>
        </label>
      </Box>
      {hasError && (
        <Box mt={1}>
          <FormHelperText error>{errors.photo}</FormHelperText>
        </Box>
      )}
    </Box>
  );
}

